import React, { useState } from "react";
import { Gem, Lock, Eye, UserPlus, Sparkles, Flame, Target, Award, TrendingUp } from "lucide-react";
import { useRecommendations } from "../context/RecommendationsContext";
import { useAuth } from "../context/AuthContext";
import AuthModal from "./AuthModal";

function VipBall({ value, accent = "gold" }) {
  const palette = {
    gold:    "from-yellow-300 via-orange-400 to-red-500",
    blue:    "from-[#1d6cb1] to-[#0e4a7a]",
    emerald: "from-emerald-400 to-teal-600",
    purple:  "from-fuchsia-500 to-purple-700",
  };
  return (
    <span
      className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br ${palette[accent]} text-white font-extrabold text-2xl shadow-lg border-2 border-white/30`}
      style={{ fontFamily: "'Playfair Display', serif" }}
    >
      {value}
    </span>
  );
}

function StatPill({ icon: Icon, label, value, color }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/25 text-white text-xs`}>
      <Icon className="w-3.5 h-3.5 text-yellow-300" />
      <span className="text-yellow-200 font-semibold">{label}:</span>
      <span className="font-bold">{value}</span>
    </div>
  );
}

export default function VipNumber() {
  const [revealed, setRevealed] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const { data } = useRecommendations();
  const { isAuthenticated } = useAuth();
  const locked = !isAuthenticated;

  // Sanitize: backend used to send literal "**-**" in some places
  const safe = (v, fallback = "--") => {
    if (!v || typeof v !== "string") return fallback;
    if (v.includes("*")) return fallback;
    return v;
  };

  const palePart = safe(data?.vip_pale, "00-00").split("-");
  const a = palePart[0] || "00";
  const b = palePart[1] || "00";
  const tripleta = (data?.daily_triplet || ["00", "00", "00"]).slice(0, 3);
  const strong = safe(data?.single_strong || data?.recommended_pale, "0000");
  const twoStrong = safe(data?.two_strong, "0000");
  const stats = data?.stats || {};
  const hotTop = (stats.hot || []).slice(0, 3).join(", ") || "—";
  const coldTop = (stats.cold || []).slice(0, 2).join(", ") || "—";
  const analyzed = stats.total_history || 0;

  const handleClick = () => {
    if (locked) {
      setAuthOpen(true);
      return;
    }
    setRevealed((v) => !v);
  };

  return (
    <section className="py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="relative overflow-hidden rounded-3xl shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0e4a7a] via-[#1d6cb1] to-[#0e4a7a]" />
          <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #f5b417 0%, transparent 30%), radial-gradient(circle at 80% 80%, #2a83ce 0%, transparent 35%)' }} />

          <div className="relative p-8 sm:p-12 text-white">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-yellow-400/20 border border-yellow-300/40 text-yellow-200 text-xs font-semibold mb-4">
                <Gem className="w-4 h-4" /> EXCLUSIVO PARA REGISTRADOS · PROMETEO
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold mb-2">Análisis VIP del Día</h2>
              <p className="text-blue-100 max-w-2xl mx-auto mb-6 text-sm sm:text-base">
                {locked
                  ? "Regístrate gratis para desbloquear el palé VIP, la tripleta del día, los números fuertes y el análisis estadístico personalizado de Prometeo."
                  : "Análisis matemático completo de Prometeo. Datos basados en los últimos sorteos reales y la numerología del día."}
              </p>
            </div>

            {/* Locked view (single CTA) */}
            {locked && (
              <div className="text-center">
                <div className="relative inline-flex flex-col items-center">
                  <div className="min-w-[280px] px-10 py-8 rounded-2xl bg-white/10 backdrop-blur-md border border-white/30 blur-sm">
                    <p className="text-xs uppercase tracking-widest text-yellow-200 mb-2">Palé VIP</p>
                    <p className="text-5xl sm:text-6xl font-extrabold text-white drop-shadow" style={{ fontFamily: "Playfair Display, serif" }}>00-00</p>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <Lock className="w-10 h-10 text-yellow-300" />
                  </div>
                </div>
                <div className="mt-6">
                  <button
                    onClick={handleClick}
                    data-testid="vip-register-btn"
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-[#0e4a7a] font-bold shadow-lg hover:shadow-xl hover:scale-[1.03] transition"
                  >
                    <UserPlus className="w-4 h-4" /> Registrarme Gratis para ver el VIP
                  </button>
                </div>
              </div>
            )}

            {/* Unlocked view: rich panel */}
            {!locked && (
              <>
                {!revealed ? (
                  <div className="text-center">
                    <div className="relative inline-flex flex-col items-center">
                      <div className="min-w-[280px] px-10 py-8 rounded-2xl bg-white/10 backdrop-blur-md border border-white/30 blur-sm">
                        <p className="text-xs uppercase tracking-widest text-yellow-200 mb-2">Palé VIP</p>
                        <p className="text-5xl sm:text-6xl font-extrabold text-white drop-shadow" style={{ fontFamily: "Playfair Display, serif" }}>00-00</p>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <Eye className="w-10 h-10 text-yellow-300" />
                      </div>
                    </div>
                    <div className="mt-6">
                      <button
                        onClick={handleClick}
                        data-testid="vip-reveal-btn"
                        className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-[#0e4a7a] font-bold shadow-lg hover:shadow-xl hover:scale-[1.03] transition"
                      >
                        <Eye className="w-4 h-4" /> Ver mi análisis VIP del día
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6" data-testid="vip-revealed">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Card title="Palé VIP" icon={Gem} accent="gold">
                        <div className="flex items-center justify-center gap-3">
                          <VipBall value={a} accent="gold" />
                          <span className="text-yellow-200 text-3xl font-extrabold">·</span>
                          <VipBall value={b} accent="emerald" />
                        </div>
                      </Card>
                      <Card title="Tripleta del Día" icon={Target} accent="blue">
                        <div className="flex items-center justify-center gap-3">
                          {tripleta.map((n, i) => (
                            <VipBall key={i} value={n} accent={["blue", "gold", "purple"][i % 3]} />
                          ))}
                        </div>
                      </Card>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <Card title="Número Fuerte" icon={Flame} accent="purple">
                        <p className="text-center text-4xl font-extrabold text-yellow-200" style={{ fontFamily: "Playfair Display, serif" }}>{strong}</p>
                      </Card>
                      <Card title="Dos Números" icon={Award} accent="emerald">
                        <p className="text-center text-4xl font-extrabold text-yellow-200" style={{ fontFamily: "Playfair Display, serif" }}>{twoStrong}</p>
                      </Card>
                    </div>

                    {data?.analysis && (
                      <div className="rounded-2xl bg-white/10 border border-white/20 p-5 backdrop-blur-md">
                        <p className="text-xs uppercase tracking-widest text-yellow-200 mb-2 inline-flex items-center gap-1">
                          <Sparkles className="w-3.5 h-3.5" /> Análisis matemático de Prometeo
                        </p>
                        <p className="text-sm text-blue-50 leading-relaxed">{data.analysis}</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <StatPill icon={TrendingUp} label="HOT" value={hotTop} />
                          <StatPill icon={Sparkles} label="COLD" value={coldTop} />
                          <StatPill icon={Gem} label="Historial" value={`${analyzed} resultados`} />
                        </div>
                      </div>
                    )}

                    <div className="text-center pt-2">
                      <button
                        onClick={handleClick}
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/30 text-yellow-200 font-semibold text-xs hover:bg-white/20 transition"
                      >
                        Ocultar análisis VIP
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} defaultTab="register" />
    </section>
  );
}

function Card({ title, icon: Icon, accent, children }) {
  const dot = {
    gold: "from-yellow-300 to-orange-500",
    blue: "from-[#1d6cb1] to-[#2a83ce]",
    emerald: "from-emerald-400 to-teal-600",
    purple: "from-fuchsia-500 to-purple-700",
  };
  return (
    <div className="rounded-2xl bg-white/10 border border-white/25 backdrop-blur-md p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-7 h-7 rounded-lg bg-gradient-to-br ${dot[accent]} flex items-center justify-center`}>
          <Icon className="w-4 h-4 text-white" />
        </span>
        <p className="text-xs uppercase tracking-widest text-yellow-200 font-bold">{title}</p>
      </div>
      {children}
    </div>
  );
}
